import React, { useState } from 'react'
import { useDisclosure, useToast } from '@chakra-ui/react'
import { TailwindModal } from '../../../../../components/TailwindModal'
import { Field, Form, Formik, FormikHelpers, useFormikContext } from 'formik'
import { Button } from '../../../../../components/ui/button'
import { Check, InfoIcon, SendHorizonal, UploadIcon } from 'lucide-react'
import { TextInput } from '../../../../../components/inputs/TextInput'
import { TrashIcon } from '@heroicons/react/20/solid'
import { useAIFileUpload } from '../../../../../model/hooks/useAIFileUpload'
import { DocumentCheckIcon } from '@heroicons/react/24/solid'
import { useAccompanyingReportAiMutation } from '../../../../../model/api/ai/useAccompanyingReportAiMutation'
import { GenerativeAIContent, ProjectFormEnum, mergePreservingTarget } from 'shared'
import { AccompanyingReportFakeAIForm } from './AccompanyingReportFakeAIForm'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../components/ui/tooltip'

interface Props {
  disclosure: ReturnType<typeof useDisclosure>
  projectForm: ProjectFormEnum
}

export const AccompanyingReportAiModal: React.FC<Props> = (props) => {
  const formikContext = useFormikContext<any>()
  const { mutateAsync: askAI, isLoading } = useAccompanyingReportAiMutation()
  const [history, setHistory] = useState<GenerativeAIContent[]>([])
  const [aiFormValues, setAiFormValues] = useState<any>(undefined)
  const toast = useToast()
  const { uploadedFiles, fileIsUploading, fileInputRef, handleUpload, removeFile } =
    useAIFileUpload()

  const onSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    const response = await askAI({
      projectForm: props.projectForm,
      history,
      parts: [
        {
          text: values.query,
        },
        ...uploadedFiles.map((file) => ({
          fileData: {
            mimeType: file.file.mimeType,
            fileUri: file.file.uri,
          },
        })),
      ],
    })

    setHistory(response.data.history)
    setAiFormValues(response.data.formValues)
    toast({
      status: 'success',
      title: 'Výsledek aktualizován',
    })

    formikHelpers.resetForm()
  }

  const onAcceptResult = async () => {
    // Get current form values
    const currentValues = formikContext.values

    // Deep merge current values with AI values, giving priority to AI values
    const mergedValues = mergePreservingTarget(currentValues, aiFormValues)

    // Reset form with merged values
    formikContext.resetForm({
      values: mergedValues,
    })

    toast({
      status: 'success',
      title: 'Formulář aktualizován',
    })

    setHistory([])
    setAiFormValues(undefined)

    props.disclosure.onClose()
  }

  return (
    <TailwindModal
      disclosure={props.disclosure}
      dialogTitle="Doplnit pomocí AI"
      className="min-w-[80%]"
    >
      <div>
        {uploadedFiles.length > 0 && (
          <div className="bg-gray-50 rounded-md p-3 shadow-md mb-3">
            <span className="text-left text-sm font-bold">Přiložené soubory:</span>
            <div className="flex flex-wrap mt-3 gap-3">
              {uploadedFiles.map((file) => (
                <span
                  key={file.file.uri}
                  className="flex gap-1 items-center bg-gray-100 text-gray-900 py-1 px-2 rounded-xl max-w-[80%] text-sm text-left"
                >
                  <DocumentCheckIcon className="w-4 h-4 text-blue-600" />
                  {file.file.displayName?.slice(-30)}
                  <TrashIcon
                    className="w-4 h-4 text-red-600 cursor-pointer"
                    onClick={() => removeFile(file)}
                  />
                </span>
              ))}
              <hr />
            </div>
          </div>
        )}
      </div>
      {aiFormValues && (
        <div className="max-h-[60vh] overflow-y-auto py-3 px-3 text-left border-2 rounded-md">
          <AccompanyingReportFakeAIForm projectForm={props.projectForm} formValues={aiFormValues} />
        </div>
      )}
      {!aiFormValues && (
        <div className="flex justify-center">
          <div className="rounded-md bg-blue-50 p-3 shadow-md max-w-lg">
            <div className="flex justify-center mb-3">
              <InfoIcon className="w-8 h-8 text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-center text-lg">
                Jak tuto funkci efektivně využít?
              </h3>
              <p>
                Pro dosažení nejlepších výsledků doporučujeme nahrát veškeré relevantní podklady,
                které máte k dispozici. AI model dokáže efektivně pracovat s různými typy dokumentů,
                včetně výkresů (pdf), emailů, obrázků a dalších. Čím více podkladů, tím lepší
                výsledky lze očekávat. AI je schopna extrahovat důležité informace z dokumentů a v
                případě potřeby určité údaje i dopočítat.
              </p>
              <p className="mt-2">
                Po nahrání souborů můžete položit dotaz nebo jednoduše kliknout na tlačítko odeslat.
                AI analyzuje nahrané dokumenty a vyplní relevantní pole ve formuláři.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="py-3">
        <Formik initialValues={{ query: '' }} onSubmit={onSubmit}>
          {(formik) => (
            <Form>
              <div className="flex gap-3 w-full">
                <input
                  ref={fileInputRef}
                  className="hidden"
                  type="file"
                  multiple
                  onChange={handleUpload}
                />

                <Button
                  isLoading={fileIsUploading || isLoading}
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  size="sm"
                  variant="secondary"
                >
                  <UploadIcon className="w-5" />
                </Button>
                <div className="flex-grow">
                  <Field name="query" component={TextInput} />
                </div>
                <Tooltip>
                  <TooltipTrigger>
                    <Button isLoading={fileIsUploading || isLoading} size="sm" type="submit">
                      <SendHorizonal className="w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Odeslat do AI</TooltipContent>
                </Tooltip>
                {aiFormValues && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        isLoading={fileIsUploading || isLoading}
                        size="sm"
                        type="button"
                        className="bg-green-500"
                        onClick={onAcceptResult}
                      >
                        <Check className="w-5 text-white" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Použít výsledek</TooltipContent>
                  </Tooltip>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </TailwindModal>
  )
}
